import React, { useState, useEffect, useRef } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import {
  smsDetails,
  perspectusDetails,
  dnmToolDetails,
  ihuriroDetails,
  padelearnDetails,
  embuildDetails,
} from "../utils/project-details";

interface Project {
  name: string;
  image: any;
  web?: string;
  video?: string;
  github?: string;
  details: any;
  tag?: string;
}

/* ── Modal ── */
const Overlay = ({
  show,
  onClose,
  children,
  title,
}: {
  show: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
}) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (show) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header-row">
          <span className="modal-title-text">{title}</span>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="modal-body-inner">{children}</div>
      </div>
    </div>
  );
};

const Projects = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeDetails, setActiveDetails] = useState("");
  const [activeVideo, setActiveVideo] = useState("");

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.08 },
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const padelearn = require("../assets/images/projects/padelearn.png");
  const embuild = require("../assets/images/projects/embuild.png");
  const sms = require("../assets/images/projects/sms-img.png");
  const perspectus = require("../assets/images/projects/perspectus.jpeg");
  const ihuriro1 = require("../assets/images/projects/dash.png");
  const domain = require("../assets/images/projects/domain10.png");

  const projects: Project[] = [
    {
      name: "PERSPECTUS",
      details: perspectusDetails,
      image: perspectus,
      tag: "AI",
      video:
        "https://www.loom.com/embed/34877a4c844a4c93961423160dffe825?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    },
    {
      name: "PADELEARN",
      details: padelearnDetails,
      image: padelearn,
      tag: "EdTech",
      github: "https://github.com/codewithadelite/padelearn",
    },
    {
      name: "EMBUILD",
      details: embuildDetails,
      image: embuild,
      tag: "SaaS",
      github: "https://github.com/codewithadelite/embuild",
    },
    {
      name: "SENSORS MONITORING",
      details: smsDetails,
      image: sms,
      tag: "IoT",
      github: "https://github.com/codewithadelite/sensors-monitoring-system",
      video:
        "https://www.loom.com/embed/a789b1219cea4d76b980f5684b68bf07?sid=7fdb9866-aa35-4027-aa48-838883b75147?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    },
    {
      name: "IHURIRO",
      details: ihuriroDetails,
      image: ihuriro1,
      tag: "Dashboard",
      video:
        "https://www.loom.com/embed/e653dfc90ab242e981a8670cd7a561d8?sid=9a0893d2-f20e-4a7b-aeda-52ce20e84373?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true",
    },
    {
      name: "DNM TOOL",
      details: dnmToolDetails,
      image: domain,
      tag: "Tooling",
      github: "https://github.com/codewithadelite/domains",
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

        .projects-section {
          min-height: 100vh;
          background: #0a0a0f;
          padding: 80px 24px 100px;
          font-family: 'DM Sans', sans-serif;
          color: #f0f0f0;
          position: relative;
          overflow: hidden;
        }

        /* Grid bg */
        .projects-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        /* Orb */
        .projects-section::after {
          content: '';
          position: absolute;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(34,211,238,0.05) 0%, transparent 70%);
          top: -100px;
          left: -100px;
          pointer-events: none;
        }

        .projects-inner {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        /* Header */
        .projects-header {
          margin-bottom: 56px;
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .projects-header.visible { opacity: 1; transform: translateY(0); }

        .projects-eyebrow {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #4ade80;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .projects-eyebrow::after {
          content: '';
          width: 40px; height: 1px;
          background: #4ade80; opacity: 0.6;
        }

        .projects-title {
          font-family: 'Syne', sans-serif;
          font-size: clamp(38px, 5.5vw, 68px);
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1;
          margin: 0 0 14px 0;
        }
        .projects-title-accent {
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .projects-subtitle {
          font-size: 15px;
          font-weight: 300;
          color: rgba(240,240,240,0.4);
          max-width: 420px;
          line-height: 1.7;
          margin: 0;
        }

        /* Divider */
        .projects-divider {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, rgba(74,222,128,0.4), rgba(255,255,255,0.06) 50%, transparent);
          margin-bottom: 52px;
          opacity: 0;
          transition: opacity 0.6s ease 0.15s;
        }
        .projects-divider.visible { opacity: 1; }

        /* Grid */
        .projects-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        @media (max-width: 960px) {
          .projects-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 580px) {
          .projects-grid { grid-template-columns: 1fr; }
        }

        /* Card */
        .project-card {
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          opacity: 0;
          transform: translateY(28px) scale(0.97);
          transition:
            opacity 0.55s ease,
            transform 0.55s ease,
            border-color 0.3s ease,
            box-shadow 0.3s ease;
          cursor: default;
        }
        .project-card.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .project-card:hover {
          border-color: rgba(74,222,128,0.3);
          box-shadow: 0 0 36px rgba(74,222,128,0.08), 0 12px 40px rgba(0,0,0,0.5);
          transform: translateY(-5px) scale(1.005);
        }

        /* Image area */
        .project-img-wrap {
          position: relative;
          aspect-ratio: 16 / 9;
          overflow: hidden;
        }
        .project-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease, filter 0.3s ease;
          filter: grayscale(10%) brightness(0.9);
        }
        .project-card:hover .project-img-wrap img {
          transform: scale(1.06);
          filter: grayscale(0%) brightness(1);
        }

        /* Gradient overlay on image */
        .project-img-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, transparent 40%, rgba(10,10,15,0.85) 100%);
          pointer-events: none;
        }

        /* Tag */
        .project-tag {
          position: absolute;
          top: 12px;
          left: 12px;
          z-index: 2;
          background: rgba(10,10,15,0.75);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(74,222,128,0.3);
          border-radius: 100px;
          padding: 4px 12px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #4ade80;
        }

        /* Body */
        .project-body {
          padding: 20px 20px 24px;
          display: flex;
          flex-direction: column;
          flex: 1;
          gap: 16px;
        }

        .project-name {
          font-family: 'Syne', sans-serif;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: rgba(240,240,240,0.9);
          margin: 0;
        }

        /* Actions */
        .project-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: auto;
        }

        .proj-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 11.5px;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-decoration: none;
          border: 1px solid transparent;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.2s;
          white-space: nowrap;
        }
        .proj-btn:hover { transform: translateY(-2px); }

        .proj-btn-details {
          background: rgba(74,222,128,0.1);
          border-color: rgba(74,222,128,0.25);
          color: #4ade80;
        }
        .proj-btn-details:hover {
          background: rgba(74,222,128,0.18);
          border-color: rgba(74,222,128,0.5);
          color: #4ade80;
        }

        .proj-btn-video {
          background: rgba(34,211,238,0.08);
          border-color: rgba(34,211,238,0.2);
          color: #22d3ee;
        }
        .proj-btn-video:hover {
          background: rgba(34,211,238,0.15);
          border-color: rgba(34,211,238,0.45);
          color: #22d3ee;
        }

        .proj-btn-ghost {
          background: rgba(255,255,255,0.04);
          border-color: rgba(255,255,255,0.1);
          color: rgba(240,240,240,0.5);
        }
        .proj-btn-ghost:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.2);
          color: rgba(240,240,240,0.85);
        }

        /* Count strip */
        .projects-count {
          margin-top: 52px;
          display: flex;
          align-items: center;
          gap: 8px;
          opacity: 0;
          transition: opacity 0.6s ease 0.9s;
        }
        .projects-count.visible { opacity: 1; }
        .projects-count-line { flex: 1; height: 1px; background: rgba(255,255,255,0.07); }
        .projects-count-text {
          font-size: 11px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.2);
          white-space: nowrap;
        }

        /* ── Modal ── */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.75);
          backdrop-filter: blur(6px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        .modal-panel {
          background: #12121a;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          width: 100%;
          max-width: 860px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          animation: slideUp 0.25s ease;
          overflow: hidden;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .modal-header-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 22px 28px;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          flex-shrink: 0;
        }
        .modal-title-text {
          font-family: 'Syne', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: rgba(240,240,240,0.85);
          letter-spacing: 0.02em;
        }
        .modal-close-btn {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.05);
          color: rgba(240,240,240,0.5);
          font-size: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .modal-close-btn:hover {
          background: rgba(74,222,128,0.1);
          border-color: rgba(74,222,128,0.3);
          color: #4ade80;
        }

        .modal-body-inner {
          overflow-y: auto;
          padding: 28px;
          flex: 1;
          font-size: 15px;
          font-weight: 300;
          line-height: 1.8;
          color: rgba(240,240,240,0.6);
        }
        .modal-body-inner h1,
        .modal-body-inner h2,
        .modal-body-inner h3 {
          font-family: 'Syne', sans-serif;
          color: rgba(240,240,240,0.9);
          font-weight: 700;
          margin-top: 24px;
        }
        .modal-body-inner a { color: #4ade80; }
        .modal-body-inner code {
          background: rgba(74,222,128,0.08);
          border: 1px solid rgba(74,222,128,0.15);
          border-radius: 6px;
          padding: 2px 7px;
          font-size: 13px;
          color: #4ade80;
        }
        .modal-body-inner iframe {
          width: 100%;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.07);
        }
      `}</style>

      <section className="projects-section" ref={sectionRef}>
        <div className="projects-inner">
          {/* Header */}
          <div className={`projects-header ${visible ? "visible" : ""}`}>
            <p className="projects-eyebrow">Portfolio</p>
            <h2 className="projects-title">
              My <span className="projects-title-accent">Projects</span>
            </h2>
            <p className="projects-subtitle">
              A selection of things I've built — from SaaS platforms to AI tools
              and monitoring systems.
            </p>
          </div>

          <div className={`projects-divider ${visible ? "visible" : ""}`} />

          {/* Cards */}
          <div className="projects-grid">
            {projects.map((project, i) => (
              <div
                key={i}
                className={`project-card ${visible ? "visible" : ""}`}
                style={{ transitionDelay: visible ? `${i * 80}ms` : "0ms" }}
              >
                {/* Image */}
                <div className="project-img-wrap">
                  <img src={project.image} alt={project.name} />
                  {project.tag && (
                    <span className="project-tag">{project.tag}</span>
                  )}
                </div>

                {/* Body */}
                <div className="project-body">
                  <h3 className="project-name">{project.name}</h3>

                  <div className="project-actions">
                    <button
                      className="proj-btn proj-btn-details"
                      onClick={() => {
                        setActiveDetails(project.details());
                        setDetailsOpen(true);
                      }}
                    >
                      <i className="fa-regular fa-file-lines" />
                      Details
                    </button>

                    {project.video && (
                      <button
                        className="proj-btn proj-btn-video"
                        onClick={() => {
                          setActiveVideo(project.video!);
                          setVideoOpen(true);
                        }}
                      >
                        <i className="fa-solid fa-play" />
                        Demo
                      </button>
                    )}

                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noreferrer"
                        className="proj-btn proj-btn-ghost"
                      >
                        <i className="fa-brands fa-github" />
                        Code
                      </a>
                    )}

                    {project.web && (
                      <a
                        href={project.web}
                        target="_blank"
                        rel="noreferrer"
                        className="proj-btn proj-btn-ghost"
                      >
                        <i className="fa-solid fa-arrow-up-right-from-square" />
                        Live
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Count strip */}
          <div className={`projects-count ${visible ? "visible" : ""}`}>
            <div className="projects-count-line" />
            <span className="projects-count-text">
              {projects.length} projects
            </span>
            <div className="projects-count-line" />
          </div>
        </div>
      </section>

      {/* Details Modal */}
      <Overlay
        show={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title="Project Details"
      >
        <Markdown rehypePlugins={[rehypeRaw]}>{activeDetails}</Markdown>
      </Overlay>

      {/* Video Modal */}
      <Overlay
        show={videoOpen}
        onClose={() => setVideoOpen(false)}
        title="Project Demo"
      >
        <iframe
          title="demo"
          width="100%"
          height="400"
          src={activeVideo}
          allowFullScreen
          allow="autoplay"
        />
      </Overlay>
    </>
  );
};

export default Projects;
